import { useState, useEffect, useCallback, useRef } from 'react'
import secondStepApiV4 from 'lib/api/secondStepApiV4'

const SCHOOL_IS_NOT_LISTED_OPTION = {
  id: 0,
  companyName: 'My school is not listed',
}

const DEFAULT_SCHOOLS = [SCHOOL_IS_NOT_LISTED_OPTION]

const INITIAL_DATA = {
  countries: [],
  regions: [],
  cities: [],
  schools: DEFAULT_SCHOOLS,
}

const INITIAL_LOADING_STATES = {
  countries: false,
  regions: false,
  cities: false,
  schools: false,
}

// Helper to append "not listed" option to schools list
const appendNotListedOption = schools => [
  ...schools.filter(school => school.id !== 0),
  SCHOOL_IS_NOT_LISTED_OPTION,
]

export const useLocationData = (
  country,
  region,
  city,
  hasRegionData,
  onFormChange,
) => {
  const [data, setData] = useState(INITIAL_DATA)
  const [loadingStates, setLoadingStates] = useState(INITIAL_LOADING_STATES)
  const [error, setError] = useState(null)

  const onFormChangeRef = useRef(onFormChange)
  onFormChangeRef.current = onFormChange

  const updateDataAndNotify = useCallback(
    (updates, notificationType = null, notificationData = null) => {
      setData(prev => ({ ...prev, ...updates }))

      if (notificationType && onFormChangeRef.current) {
        onFormChangeRef.current(
          { target: { value: notificationData } },
          notificationType,
        )
      }
    },
    [],
  )

  const fetchWithErrorHandling = useCallback(
    async (fieldName, fetchFn, onSuccess, onError) => {
      setLoadingStates(prev => ({ ...prev, [fieldName]: true }))
      setError(null)

      try {
        const result = await fetchFn()
        await onSuccess(result)
      } catch (err) {
        setError(err)
        await onError(err)
      } finally {
        setLoadingStates(prev => ({ ...prev, [fieldName]: false }))
      }
    },
    [],
  )

  // Fetch countries (only once on mount)
  useEffect(() => {
    fetchWithErrorHandling(
      'countries',
      secondStepApiV4.getCountries,
      result => updateDataAndNotify({ countries: result.data || [] }),
      () => updateDataAndNotify({ countries: [] }),
    )
  }, [fetchWithErrorHandling, updateDataAndNotify])

  // Fetch regions when country changes
  useEffect(() => {
    if (!country) {
      updateDataAndNotify({ regions: [] }, 'regionData', [])
      return
    }

    fetchWithErrorHandling(
      'regions',
      () => secondStepApiV4.getRegions(country),
      result => {
        const regionsData = result.data || []
        updateDataAndNotify({ regions: regionsData }, 'regionData', regionsData)
      },
      () => updateDataAndNotify({ regions: [] }, 'regionData', []),
    )
  }, [country, fetchWithErrorHandling, updateDataAndNotify])

  // Fetch cities when country/region changes
  useEffect(() => {
    if (!country) {
      updateDataAndNotify({ cities: [] })
      return
    }

    const fetchFn = region
      ? () => secondStepApiV4.getCitiesWithRegion(country, region)
      : () => secondStepApiV4.getCitiesWithoutRegion(country)

    fetchWithErrorHandling(
      'cities',
      fetchFn,
      result => {
        const citiesData = result.data || []
        const citiesWithCurrent =
          city && !citiesData.includes(city)
            ? [city, ...citiesData]
            : citiesData
        updateDataAndNotify({ cities: citiesWithCurrent })
      },
      () => updateDataAndNotify({ cities: [] }),
    )
  }, [country, region, city, fetchWithErrorHandling, updateDataAndNotify])

  // Fetch schools when city/country/region changes
  useEffect(() => {
    if (!city || !country) {
      updateDataAndNotify({ schools: DEFAULT_SCHOOLS })
      return
    }

    const fetchFn =
      hasRegionData && region
        ? () => secondStepApiV4.getNSCustomersWithRegion(city, country, region)
        : () => secondStepApiV4.getNSCustomersWithoutRegion(city, country)

    fetchWithErrorHandling(
      'schools',
      fetchFn,
      result => {
        const schoolsWithUnlisted = appendNotListedOption(result.data || [])
        updateDataAndNotify(
          { schools: schoolsWithUnlisted },
          'netSuiteData',
          schoolsWithUnlisted,
        )
      },
      () =>
        updateDataAndNotify(
          { schools: DEFAULT_SCHOOLS },
          'netSuiteData',
          DEFAULT_SCHOOLS,
        ),
    )
  }, [
    city,
    country,
    region,
    hasRegionData,
    fetchWithErrorHandling,
    updateDataAndNotify,
  ])

  return { ...data, loadingStates, error }
}
